import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';

import css from './SelectorIdentifierLineChip.module.css';

/**
 * One selector/identifier line as a chip: truncated label, optional tooltip when truncated,
 * Edit/Delete in edit mode only. No array or row index logic.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {boolean} props.isReadOnly
 * @param {() => void} [props.onEdit]
 * @param {() => void} [props.onDelete]
 * @param {{ edit: string, delete: string }} props.ariaLabels
 * @param {string} [props.lineAriaLabel] Root accessibility name (e.g. prefix + role).
 * @param {string} [props.className]
 * @param {boolean} [props.wrapText] Show the full value instead of ellipsizing it.
 */
export default function SelectorIdentifierLineChip({
    value,
    isReadOnly,
    onEdit,
    onDelete,
    ariaLabels,
    lineAriaLabel,
    className = '',
    wrapText = false
}) {
    const labelRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const measure = useCallback(() => {
        const el = labelRef.current;
        if (!el) return;
        setIsTruncated(el.scrollWidth > el.clientWidth + 0.5);
    }, []);

    useLayoutEffect(() => {
        measure();
        const el = labelRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [value, measure]);

    const labelEl = (
        <span ref={labelRef} className={wrapText ? css.labelWrapped : css.label}>
            {value}
        </span>
    );

    const inner = (
        <Box
            className={`${wrapText ? css.rootWrapped : css.root} ${isReadOnly ? css.rootReadOnly : ''} ${className}`}
            aria-label={lineAriaLabel}
            component="span"
            display="inline-flex"
            alignItems={wrapText ? 'flex-start' : 'center'}
        >
            <span className={css.labelWrap}>{labelEl}</span>
            {!isReadOnly && (
                <span className={css.actions}>
                    <IconButton
                        size="small"
                        className={css.actionButton}
                        aria-label={ariaLabels.edit}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit?.();
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        className={css.actionButton}
                        aria-label={ariaLabels.delete}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.();
                        }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </span>
            )}
        </Box>
    );

    if (!wrapText && isTruncated) {
        return (
            <Tooltip title={value}>
                <span style={{ display: 'inline-flex', maxWidth: '100%' }}>{inner}</span>
            </Tooltip>
        );
    }

    return <span style={{ display: 'inline-flex', maxWidth: '100%' }}>{inner}</span>;
}
